import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, ChevronDown, ChevronRight, Loader2, Send } from "lucide-react";
import { reApi, errMsg } from "@/lib/residentApi";
import { useAllyDetail } from "@/lib/allyContext";
import { btnGold, field } from "@/components/nds/windows/finance/parts";
import AreaFix from "@/components/nds/windows/checkin/AreaFix";
import { DraftQuestions } from "@/components/nds/windows/ReCheckinsContent";
import StaffLogPaycheck from "@/components/nds/windows/session/StaffLogPaycheck";
import { PendingCharges, SessionCtx } from "@/components/nds/windows/session/SessionLoan";
import SessionHelp from "@/components/nds/windows/session/SessionHelp";
import { ClearMistake } from "@/components/nds/ClearMistake";
import AssignAllPillars from "@/components/nds/windows/session/AssignAllPillars";
import WorkWeekEditor from "@/components/portal/lp/WorkWeekEditor";
import axios from "axios";

const LP = `${process.env.REACT_APP_BACKEND_URL}/api/lp`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("nex_token")}` } });
const lpStaff = { get: (p, params) => axios.get(`${LP}${p}`, { ...auth(), params }).then((x) => x.data), put: (p, b) => axios.put(`${LP}${p}`, b, auth()).then((x) => x.data) };

const MIN = 5;
const etToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
export const STEPS = [
  { key: "talk", label: "Conversation", areas: ["questions"] },
  { key: "money", label: "Money", areas: ["paychecks", "rent"] },
  { key: "work", label: "Work schedule", areas: ["schedule"] },
  { key: "pillars", label: "Pillars", areas: ["pillars"] },
  { key: "life", label: "Life on campus", areas: ["incidents", "memos", "room", "intake"] },
  { key: "review", label: "Weekly review", areas: ["review"] },
  { key: "submit", label: "Submit", areas: [] },
];
const inflight = {};
/** One draft per resident even if the effect runs twice; a 409 means another call just made it. */
const openDraft = (residentId) => inflight[residentId] || (inflight[residentId] = (async () => {
  try {
    const p = await reApi.get("/checkins/areas-preview", { resident_id: residentId });
    if (p.draft_id) return p.draft_id;
    try { return (await reApi.post("/checkins", { resident_id: residentId, checkin_date: etToday() })).id; }
    catch (e) {
      const again = await reApi.get("/checkins/areas-preview", { resident_id: residentId });
      if (again.draft_id) return again.draft_id;
      throw e;
    }
  } finally { setTimeout(() => { delete inflight[residentId]; }, 2000); }
})());
const FIX_ONCE = { intake: "room" }; // room + intake share the profile tab
const covered = (a, notes) => a.ok || (notes[a.key] || "").trim().length >= MIN;

function AreaBlock({ a, rec, note, setNote, onFixed, saveNotes, hideFix }) {
  const [open, setOpen] = useState(!a.ok);
  return (
    <section className="border border-white/10 rounded-sm" data-testid={`ss-area-${a.key}`}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`ss-area-toggle-${a.key}`} className="w-full flex items-start gap-2 p-3 text-left">
        {a.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-300 shrink-0 mt-0.5" /> : <AlertTriangle className={`h-4 w-4 shrink-0 mt-0.5 ${covered(a, { [a.key]: note }) ? "text-sky-300" : "text-yellow-300"}`} />}
        <span className="min-w-0 flex-1"><span className="block text-sm text-nex-silver font-medium">{a.label}</span><span className="block text-xs text-nex-muted break-words">{a.text}</span></span>
        {open ? <ChevronDown className="h-3.5 w-3.5 text-nex-muted" /> : <ChevronRight className="h-3.5 w-3.5 text-nex-muted" />}
      </button>
      {open && <div className="px-3 pb-3 space-y-2">
        {a.key !== "questions" && <SessionHelp cid={rec.id} area={a} residentId={rec.resident_id} onChanged={onFixed} />}
        {a.key === "questions" && !a.ok && <ClearMistake residentId={rec.resident_id} alertKey="questions" onCleared={onFixed} testid="ss-clear-questions" />}
        {!hideFix && a.key !== "questions" && <div className="p-3 border border-white/5 rounded-sm bg-black/20" data-testid={`ss-fix-${a.key}`}>
          <AreaFix area={a.key} residentId={rec.resident_id} residentName={rec.resident_name} onChanged={onFixed} onClose={onFixed} /></div>}
        {!a.ok && <input value={note || ""} onChange={(e) => setNote(e.target.value)} onBlur={saveNotes} placeholder="Not done today? Note why (required to submit)"
          data-testid={`ss-note-${a.key}`} className={`${field} block w-full box-border`} />}
      </div>}
    </section>
  );
}

function SubmitStep({ areas, notes, onSubmit, busy, result, basics }) {
  if (result) return (
    <div className="p-4 border border-emerald-400/40 rounded-sm space-y-1" data-testid="ss-done">
      <div className="text-emerald-300 font-medium">Check-in submitted</div>
      <div className="text-xs text-nex-muted">{result.counted ? "Counted for this week." : `Not counted: ${result.not_counted_reason || "too soon"}.`} Every area and note was saved with it.</div>
    </div>);
  const ordered = STEPS.flatMap((s) => s.areas).map((k) => areas.find((a) => a.key === k)).filter(Boolean);
  const missing = [...(basics ? [{ label: `${basics} (Conversation step)` }] : []), ...ordered.filter((a) => !covered(a, notes))];
  return (
    <div className="space-y-3" data-testid="ss-submit-step">
      <ul className="space-y-1">{ordered.map((a) => (
        <li key={a.key} className="flex items-start gap-2 text-sm" data-testid={`ss-sum-${a.key}`}>
          {a.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-300 shrink-0" /> : covered(a, notes) ? <AlertTriangle className="h-4 w-4 text-sky-300 shrink-0" /> : <AlertTriangle className="h-4 w-4 text-yellow-300 shrink-0" />}
          <span className="text-nex-silver">{a.label}</span><span className="text-xs text-nex-muted break-words min-w-0">{a.ok ? "complete" : covered(a, notes) ? `noted: ${notes[a.key]}` : notes[a.key] ? "note too short (5+ letters)" : "needs a fix or a note"}</span>
        </li>))}</ul>
      <button type="button" disabled={busy || missing.length > 0} onClick={onSubmit} data-testid="ss-submit" className={`${btnGold} inline-flex items-center gap-1.5`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Submit check-in</button>
      {missing.length > 0 && <p className="text-xs text-yellow-300" data-testid="ss-missing">Still open: {missing.map((a) => a.label).join(", ")}</p>}
    </div>
  );
}

/** Sit-Down Session: one resident, one screen, step by step. Conversation first, then every fix, then submit the weekly check-in. */
export default function SessionContent({ win }) {
  const { residentId, name } = win.payload;
  const [rec, setRec] = useState(null);
  const [areas, setAreas] = useState(null);
  const [notes, setNotes] = useState({});
  const [step, setStep] = useState(0);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [bump, setBump] = useState(0);
  useAllyDetail(win.id, { step: STEPS[step]?.label });
  const loadAreas = (id) => reApi.get(`/checkins/${id}/areas`).then((x) => {
    setAreas(x.areas);
    setNotes((n) => ({ ...Object.fromEntries(x.areas.map((a) => [a.key, a.note || ""])), ...Object.fromEntries(Object.entries(n).filter(([, v]) => v)) }));
  });
  const loadRec = (id) => reApi.get(`/checkins/${id}`).then(setRec);
  const reload = () => rec && Promise.all([loadRec(rec.id), loadAreas(rec.id)]).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    (async () => {
      try {
        const id = await openDraft(residentId);
        await Promise.all([loadRec(id), loadAreas(id)]);
        setErr(null);
      } catch (e) { setErr(errMsg(e)); }
    })();
  }, [residentId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err && !rec) return <div className="p-5 text-sm text-yellow-300" data-testid="ss-error">{err}</div>;
  if (!rec || !areas) return <div className="p-5"><Loader2 className="h-5 w-5 animate-spin text-nex-gold" /></div>;
  const byKey = Object.fromEntries(areas.map((a) => [a.key, a]));
  const saveNotes = () => reApi.patch(`/checkins/${rec.id}/area-notes`, { notes }).catch((e) => toast.error(errMsg(e)));
  const go = async (i) => { if (result) return; await saveNotes(); await reload(); setStep(i); document.querySelector('[data-testid="ss-root"]')?.scrollIntoView({ block: "start" }); };
  const submit = async () => {
    setBusy(true);
    try { await saveNotes(); const r = await reApi.patch(`/checkins/${rec.id}/submit`); setResult(r); toast.success("Check-in submitted"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const S = STEPS[step];
  const basics = !rec.employment_status || rec.mood_rating == null ? "Employment & mood" : null;
  const stepState = (s) => (s.key === "submit" ? (result ? "done" : "todo") : (s.key === "talk" && basics) ? "todo" : s.areas.every((k) => byKey[k] && covered(byKey[k], notes)) ? "done" : "todo");
  if (result) return <div className="p-5 space-y-3" data-testid="ss-root"><div className="font-serif text-lg text-nex-silver">Sit-down with {name || rec.resident_name}</div><SubmitStep result={result} /></div>;
  return (
    <div className="p-5 space-y-4" data-testid="ss-root">
      <div>
        <div className="font-serif text-lg text-nex-silver" data-testid="ss-title">Sit-down with {name || rec.resident_name}</div>
        <div className="text-xs text-nex-muted">Weekly check-in {rec.checkin_date} · Week {rec.week_number ?? rec.program_week ?? "—"}</div>
      </div>
      <ol className="flex flex-wrap gap-1.5" data-testid="ss-steps">
        {STEPS.map((s, i) => (
          <li key={s.key}><button type="button" onClick={() => go(i)} data-testid={`ss-step-${s.key}`}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border font-mono text-[10px] uppercase tracking-[0.12em] ${i === step ? "border-nex-gold text-nex-gold bg-nex-gold/10" : stepState(s) === "done" ? "border-emerald-400/40 text-emerald-300" : "border-white/15 text-nex-muted"}`}>
            {stepState(s) === "done" ? <CheckCircle2 className="h-3 w-3" /> : <span>{i + 1}</span>} {s.label}</button></li>))}
      </ol>
      <SessionCtx.Provider value={rec.id}><div className="space-y-3" data-testid={`ss-body-${S.key}`}>
        {S.key === "talk" && <DraftQuestions rec={rec} reload={reload} />}
        {S.key === "work" && <div className="p-3 border border-nex-gold/30 rounded-sm" data-testid="ss-work-week"><WorkWeekEditor staff api={lpStaff} rid={rec.resident_id} onSaved={reload} /></div>}
        {S.key === "pillars" && <AssignAllPillars key={`aa-${bump}`} residentId={rec.resident_id} onDone={() => { setBump((b) => b + 1); reload(); }} />}
        {S.key === "money" && <StaffLogPaycheck residentId={rec.resident_id} onDone={() => { setBump((b) => b + 1); reload(); }} />}
        {S.key === "money" && <PendingCharges key={`pc-${bump}`} residentId={rec.resident_id} onChanged={() => setBump((b) => b + 1)} />}
        {S.key === "submit" ? <SubmitStep areas={areas} notes={notes} onSubmit={submit} busy={busy} basics={basics} />
          : S.areas.map((k) => byKey[k] && <AreaBlock key={`${k}-${rec.id}-${bump}`} a={byKey[k]} rec={rec} note={notes[k]} setNote={(v) => setNotes((x) => ({ ...x, [k]: v }))}
            onFixed={reload} saveNotes={saveNotes} hideFix={k === "schedule" || (FIX_ONCE[k] && S.areas.includes(FIX_ONCE[k]))} />)}
      </div></SessionCtx.Provider>
      <div className="flex items-center gap-2 pt-2 border-t border-white/10">
        {step > 0 && <button type="button" onClick={() => go(step - 1)} data-testid="ss-back" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.16em] text-nex-muted hover:text-nex-silver"><ArrowLeft className="h-3 w-3" /> Back</button>}
        {step < STEPS.length - 1 && <button type="button" onClick={() => go(step + 1)} data-testid="ss-next" className={`${btnGold} ml-auto inline-flex items-center gap-1`}>Next: {STEPS[step + 1].label} <ArrowRight className="h-3 w-3" /></button>}
      </div>
    </div>
  );
}
